import { useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { savePreviewResults, setExtractionResults, setActiveLoanId, clearResults, clearAllFiles } from '../store/slices/documentsSlice';
import { clearLoanData } from '../store/slices/databaseSlice';
import { addNotification } from '../store/slices/uiSlice';
import { reset as resetProgress } from '../store/slices/progressSlice';

/**
 * Custom hook to determine what view to display on the Document Analysis page
 * This centralizes all the complex logic for determining the current state
 * 
 * BULLETPROOF LOGIC:
 * 1. If we have extraction results with field data (excluding _metadata), show results
 * 2. If we have analysis results, show results  
 * 3. Only redirect if we have NO data and are not loading
 */
export const useDocumentAnalysisDisplay = ({
  loanId,
  activeLoanId,
  extractionResults,
  analysisResults,
  isLoadingFromDatabase,
  isActive,
  isAnimating,
  proposalFile,
  noteFile,
  securityFile,
  loadLoanData,
  isPreviewExtractionRunning = false,
  isModalOpen = false
}) => {
  let navigate;
  try {
    navigate = useNavigate();
  } catch (error) {
    // useNavigate() failed (likely in test environment), make it optional
    navigate = null;
  }
  const dispatch = useDispatch();
  const location = useLocation();
  const hasLoadedInitialData = useRef(false);
  
  // Determine if we have any uploaded files
  const hasFiles = Boolean(proposalFile || noteFile || securityFile);
  
  // Simple: show loading only when extractionResults is null or empty object
  const showLoadingBar = useMemo(() => {
    if (!extractionResults) {
      return true;
    }
    
    const keys = Object.keys(extractionResults);
    return keys.length === 0;
  }, [extractionResults]);
  
  // hasResults is just the opposite
  const hasResults = !showLoadingBar;
  
  // CRITICAL: Clear stale data when loanId changes
  useEffect(() => {
    if (loanId && activeLoanId && activeLoanId !== loanId) {
      console.log('🔄 [useDocumentAnalysisDisplay] LoanId changed from', activeLoanId, 'to', loanId, '- clearing stale data');
      
      // Clear all stale data immediately
      dispatch(clearResults());
      dispatch(setExtractionResults(null));
      dispatch(clearAllFiles());
      dispatch(clearLoanData());
      dispatch(resetProgress()); // Reset progress bar to clear isActive state
      
      // Reset the loaded data flag so new data will be loaded
      hasLoadedInitialData.current = false;
    }
    
    // Update the active loanId in Redux
    if (loanId && activeLoanId !== loanId) {
      dispatch(setActiveLoanId(loanId));
    }
  }, [loanId, activeLoanId, dispatch]);
  
  // Load data on mount if we have a loanId and are not coming from extraction results
  useEffect(() => {
    if (loanId && loadLoanData && !hasLoadedInitialData.current) {
      const urlParams = new URLSearchParams(location.search);
      const isFromExtraction = urlParams.get('extraction') === 'true';
      
      // If extraction=true but we have no results, clear the parameter and load from database
      if (isFromExtraction && !hasResults) {
        console.log('🔄 [useDocumentAnalysisDisplay] extraction=true but no results found - clearing parameter and loading from database');
        const url = new URL(window.location);
        url.searchParams.delete('extraction');
        window.history.replaceState({}, '', url.toString());
        hasLoadedInitialData.current = true;
        loadLoanData();
      } else if (!isFromExtraction) {
        // Only load data if we're not coming from extraction
        console.log('🔄 [useDocumentAnalysisDisplay] Loading initial data for loan:', loanId);
        hasLoadedInitialData.current = true;
        loadLoanData();
      }
    }
  }, [loanId, loadLoanData, location.search, hasResults]);
  
  // Handle redirect when we have a loanId but no results
  // CRITICAL: Only redirect if we have NO data and are not actively loading/processing
  // IMPORTANT: Do NOT redirect when preview extraction is running OR when modal is open
  useEffect(() => {
    const shouldRedirect = loanId && navigate && !hasResults && !isLoadingFromDatabase && !isActive && !isAnimating && !isPreviewExtractionRunning && !isModalOpen;
    
    // Debug: Log redirect decision
    if (loanId && !hasResults) {
      console.log('🔍 [REDIRECT DEBUG] Checking redirect conditions:', {
        loanId: !!loanId,
        navigate: !!navigate,
        hasResults,
        isLoadingFromDatabase,
        isActive,
        isAnimating,
        isPreviewExtractionRunning,
        isModalOpen,
        shouldRedirect
      });
    }
    
    if (shouldRedirect) {
      console.log('🔄 [REDIRECT] Navigating to /document-analysis/' + loanId + '/scans');
      navigate(`/document-analysis/${loanId}/scans`);
    }
  }, [loanId, navigate, hasResults, isLoadingFromDatabase, isActive, isAnimating, isPreviewExtractionRunning, isModalOpen]);
  
  // Determine the current view state
  const viewState = useMemo(() => {
    // Show results if we have them
    if (hasResults) {
      return {
        view: 'results',
        showLoanSelector: false,
        showUpload: false,
        showResults: true,
        showLoading: false,
        showProgress: false
      };
    }
    
    // Show loading when we don't have results - PERIOD
    return {
      view: 'loading',
      showLoanSelector: false,
      showUpload: false,
      showResults: false,
      showLoading: true,
      showProgress: false
    };
  }, [hasResults]);

  // Determine which results data to show
  const resultsData = useMemo(() => {
    // Priority: extraction results > analysis results (extraction is more recent/accurate)
    if (extractionResults) {
      // Handle different data structures
      if (extractionResults.match_result && Object.keys(extractionResults.match_result).length > 0) {
        return extractionResults.match_result;
      }
      if (extractionResults.extraction_result && Object.keys(extractionResults.extraction_result).length > 0) {
        return extractionResults.extraction_result;
      }
      // For database results, return the extraction results directly (they have field data as properties)
      const fieldKeys = Object.keys(extractionResults).filter(key => key !== '_metadata');
      if (fieldKeys.length > 0) {
        return extractionResults;
      }
    }
    
    // Fallback to analysis results if no extraction results
    if (analysisResults && Object.keys(analysisResults).length > 0) {
      return analysisResults;
    }
    
    return null;
  }, [extractionResults, analysisResults]);

  // Save and refresh functionality
  const saveAndRefresh = async () => {
    if (!resultsData || Object.keys(resultsData).length === 0) {
      dispatch(addNotification({
        type: 'warning',
        message: '⚠️ No results to save. Please extract values first.',
        duration: 5000
      }));
      return;
    }

    try {
      // Save the current results
      const saveResult = await dispatch(savePreviewResults({
        matchedResults: resultsData,
        loanId
      }));

      if (saveResult.type.endsWith('/fulfilled')) {
        dispatch(addNotification({
          type: 'success',
          message: '✅ Results saved successfully!',
          duration: 3000
        }));

        // Reload the data from database to get the saved results
        if (loadLoanData) {
          await loadLoanData(true);
        }
      } else {
        throw new Error(saveResult.payload || 'Save failed');
      }
    } catch (error) {
      console.error('Save and refresh error:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to save results: ${error.message}`,
        duration: 5000
      }));
    }
  };

  return {
    // View state flags
    showLoanSelector: viewState.showLoanSelector,
    showUpload: viewState.showUpload,
    showResults: viewState.showResults,
    showLoading: viewState.showLoading,
    showProgress: viewState.showProgress,
    
    // Current view
    currentView: viewState.view,
    
    // Data
    resultsData,
    hasFiles,
    hasResults,
    
    // Actions
    saveAndRefresh
  };
};